"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NoCustomLabelsInfoText = exports.DropDownLabelWithTooltip = exports.ContentNameAndIcon = exports.useCustomLabelsDropdown = exports.DeleteCustomLabelDialog = exports.DeleteCustomLabelButton = exports.EditMessagePopover = void 0;
const tslib_1 = require("tslib");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_messages_1 = require("@tableau/catalog-messages");
const NotificationInfoOutlineIcon_1 = require("@tableau/icons/NotificationInfoOutlineIcon");
const PencilBaseIcon_1 = require("@tableau/icons/PencilBaseIcon");
const TrashBaseIcon_1 = require("@tableau/icons/TrashBaseIcon");
const widgets_1 = require("@tableau/widgets");
const react_query_1 = require("@tanstack/react-query");
const react_1 = tslib_1.__importDefault(require("react"));
const LabelHelpers_1 = require("../LabelHelpers");
const LabelValueSorting_1 = require("../LabelValueSorting");
const labelCategories_1 = require("../restApi/labelCategories");
const Labels_1 = require("../restApi/Labels");
const labelValues_1 = require("../restApi/labelValues");
const Styles_1 = require("../Styles");
const editMessageTestId = 'edit-custom-label-message';
const MESSAGE_MAXIMUM_LENGTH = 4000;
const inBuiltCategories = ['WARNING', 'CERTIFICATION', 'SENSITIVITY'];
function isInBuiltCategory(category) {
    return inBuiltCategories.includes(category);
}
// Edit & Delete buttons and dialogs
function EditMessageButton(hasMessage) {
    const editButtonProps = {
        title: catalog_messages_1.Messages.CustomLabelDialogEditMessageButtonTitle(),
        buttonType: widgets_1.ButtonType.Icon,
        testId: 'custom-label-dialog-edit-message-button',
        style: {
            padding: `${widgets_1.TabStyles.Spacing.SmallSpace}px`,
            marginLeft: 6,
        },
        getContent: (_) => {
            return react_1.default.createElement(PencilBaseIcon_1.PencilBaseIcon, { size: 14, alt: catalog_messages_1.Messages.CustomLabelDialogEditMessageButtonTitle() });
        },
    };
    const addButtonProps = {
        title: catalog_messages_1.Messages.CustomLabelDialogAddMessageButtonTitle(),
        buttonType: widgets_1.ButtonType.Icon,
        testId: 'custom-label-dialog-add-message-button',
        style: {
            padding: `${widgets_1.TabStyles.Spacing.SmallSpace}px`,
            width: 56,
            height: 24,
        },
        getContent: (_) => (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(PencilBaseIcon_1.PencilBaseIcon, { size: 14, alt: catalog_messages_1.Messages.CustomLabelDialogAddMessageButtonTitle() }),
            react_1.default.createElement("span", { style: { marginLeft: 3 } }, catalog_messages_1.Messages.CustomLabelDialogAddButton()))),
    };
    if (hasMessage) {
        return react_1.default.createElement(widgets_1.ButtonWidget, Object.assign({}, editButtonProps, { "aria-label": catalog_messages_1.Messages.CustomLabelDialogEditMessageButtonTitle() }));
    }
    return react_1.default.createElement(widgets_1.ButtonWidget, Object.assign({}, addButtonProps, { "aria-label": catalog_messages_1.Messages.CustomLabelDialogAddMessageButtonTitle() }));
}
function EditMessagePopover({ contentItem, message: initialMessage, labelValue, onClose: refetchOnClose }) {
    const uiActions = (0, catalog_core_1.useUIActions)();
    const queryClient = (0, react_query_1.useQueryClient)();
    const popoverInstance = react_1.default.useRef(null);
    const { value: message, valid: messageValid, element: MessageWidget } = (0, catalog_core_1.useTextFormatter)({
        initialState: initialMessage !== null && initialMessage !== void 0 ? initialMessage : '',
        label: '',
        testId: editMessageTestId,
        max: MESSAGE_MAXIMUM_LENGTH,
    });
    const onClose = react_1.default.useCallback(() => {
        var _a;
        refetchOnClose();
        (_a = popoverInstance.current) === null || _a === void 0 ? void 0 : _a.toggle(false);
    }, [refetchOnClose, popoverInstance]);
    const onSuccess = react_1.default.useCallback(() => tslib_1.__awaiter(this, void 0, void 0, function* () {
        yield queryClient.invalidateQueries({ queryKey: ['labels', contentItem.contentType, contentItem.contentId] });
        uiActions.popToast('success', catalog_messages_1.Messages.CustomLabelDialogMessageSuccessToast());
        onClose();
    }), [contentItem, queryClient, uiActions, onClose]);
    const mutationFn = (0, catalog_core_1.useRestFn)(Labels_1.setOrDeleteLabel);
    const submit = (0, react_query_1.useMutation)({
        mutationFn,
        onSuccess: () => onSuccess(),
        onError: () => {
            var _a;
            return uiActions.popToast('error', catalog_messages_1.Messages.CustomLabelDialogErrorToast({
                CONTENT_NAME: (_a = contentItem.contentName) !== null && _a !== void 0 ? _a : '',
            }));
        },
    });
    const hasMessage = initialMessage !== null && (initialMessage === null || initialMessage === void 0 ? void 0 : initialMessage.trim()) !== '';
    const submitDisabled = messageValid !== undefined || submit.isLoading;
    return (react_1.default.createElement("div", null,
        hasMessage && (react_1.default.createElement("span", { style: {
                display: 'inline-block',
                overflow: 'hidden',
                maxWidth: '90%',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            } }, initialMessage)),
        react_1.default.createElement("span", { style: { position: 'absolute' } },
            react_1.default.createElement(widgets_1.PopoverWidget, { "aria-describedby": 'popoverDescription', footerButtons: [
                    { disabled: submit.isLoading, children: catalog_messages_1.Messages.Cancel(), onClick: onClose },
                    {
                        disabled: submitDisabled,
                        showSpinner: submit.isLoading,
                        children: catalog_messages_1.Messages.Save(),
                        buttonType: widgets_1.ButtonType.Primary,
                        onClick: () => submit.mutate({
                            labelContent: { message, elevated: false, value: labelValue, active: true },
                            contentItems: [contentItem],
                            delayOverride: 0,
                        }),
                    },
                ], placement: 'right-start', popoverContent: react_1.default.createElement("form", null, MessageWidget), ref: popoverInstance, showTitleBar: true, title: react_1.default.createElement("span", { style: { fontWeight: 'normal' } }, catalog_messages_1.Messages.CustomLabelMessageDialogTitle()), width: 400, style: { fontWeight: 'lighter' }, triggerElement: EditMessageButton(hasMessage) }))));
}
exports.EditMessagePopover = EditMessagePopover;
/** @deprecated - this and DeleteCustomLabelDialog are almost entirely the same */
function DeleteCustomLabelButton({ luid, contentItem, labelValue, shouldShowDialog, onClose: refetchOnClose }) {
    const queryClient = (0, react_query_1.useQueryClient)();
    const uiActions = (0, catalog_core_1.useUIActions)();
    const anchorRef = react_1.default.useRef(null);
    const [isShown, setShowingDialog] = react_1.default.useState(false);
    const showDialog = react_1.default.useCallback(() => setShowingDialog(true), [setShowingDialog]);
    const hideDialog = react_1.default.useCallback(() => setShowingDialog(false), [setShowingDialog]);
    const onSuccess = react_1.default.useCallback((contentName) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        uiActions.popToast('success', catalog_messages_1.Messages.CustomLabelDialogSuccessToast({
            CONTENT_NAME: contentName,
        }));
        refetchOnClose();
        yield queryClient.invalidateQueries({ queryKey: ['labels', contentItem.contentType, contentItem.contentId] });
    }), [refetchOnClose, contentItem, queryClient, uiActions]);
    const mutationFn = (0, catalog_core_1.useRestFn)(Labels_1.setOrDeleteLabel);
    const submit = (0, react_query_1.useMutation)({
        mutationFn,
        onSuccess: () => { var _a; return onSuccess((_a = contentItem.contentName) !== null && _a !== void 0 ? _a : ''); },
        onError: () => {
            var _a;
            return uiActions.popToast('error', catalog_messages_1.Messages.CustomLabelDialogErrorToast({
                CONTENT_NAME: (_a = contentItem.contentName) !== null && _a !== void 0 ? _a : '',
            }));
        },
    });
    const buttonProps = {
        title: catalog_messages_1.Messages.CustomLabelDialogDeleteButtonTitle(),
        buttonType: widgets_1.ButtonType.Icon,
        testId: 'custom-label-dialog-delete-button',
        style: {
            padding: `${widgets_1.TabStyles.Spacing.SmallSpace}px`,
            width: 30,
            height: 24,
            marginLeft: 6,
        },
        getContent: (_) => {
            return react_1.default.createElement(TrashBaseIcon_1.TrashBaseIcon, { size: 14, alt: catalog_messages_1.Messages.CustomLabelDialogDeleteButtonTitle() });
        },
    };
    return (react_1.default.createElement("span", { ref: anchorRef },
        react_1.default.createElement(widgets_1.ButtonWidget, Object.assign({}, buttonProps, { "aria-label": catalog_messages_1.Messages.CustomLabelDialogDeleteButtonTitle(), showSpinner: submit.isLoading, onClick: () => {
                if (shouldShowDialog()) {
                    showDialog();
                }
                else {
                    // deletes the label if no message
                    submit.mutate({
                        labelContent: { message: '', elevated: false, value: labelValue, active: false },
                        contentItems: [contentItem],
                        luid,
                        delayOverride: 0,
                    });
                }
            }, disabled: isShown || submit.isLoading })),
        isShown && (react_1.default.createElement(DeleteCustomLabelDialog, { luid: luid, contentItem: contentItem, labelValue: labelValue, hideDialog: hideDialog, onClose: refetchOnClose }))));
}
exports.DeleteCustomLabelButton = DeleteCustomLabelButton;
function DeleteCustomLabelDialog({ luid, contentItem, labelValue, hideDialog, onClose: refetchOnClose }) {
    const uiActions = (0, catalog_core_1.useUIActions)();
    const queryClient = (0, react_query_1.useQueryClient)();
    const onSuccess = react_1.default.useCallback((contentName) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        uiActions.popToast('success', catalog_messages_1.Messages.CustomLabelDialogSuccessToast({
            CONTENT_NAME: contentName,
        }));
        refetchOnClose();
        hideDialog();
        yield queryClient.invalidateQueries({ queryKey: ['labels', contentItem.contentType, contentItem.contentId] });
    }), [refetchOnClose, contentItem, queryClient, uiActions, hideDialog]);
    const mutationFn = (0, catalog_core_1.useRestFn)(Labels_1.setOrDeleteLabel);
    const submit = (0, react_query_1.useMutation)({
        mutationFn,
        onSuccess: () => { var _a; return onSuccess((_a = contentItem.contentName) !== null && _a !== void 0 ? _a : ''); },
        onError: () => {
            var _a;
            return uiActions.popToast('error', catalog_messages_1.Messages.CustomLabelDialogErrorToast({
                CONTENT_NAME: (_a = contentItem.contentName) !== null && _a !== void 0 ? _a : '',
            }));
        },
    });
    const confirmDeleteText = catalog_messages_1.Messages.CustomLabelDialogDeleteConfirmationText();
    const submitDisabled = submit.isLoading;
    const footerContent = (react_1.default.createElement("div", { className: Styles_1.FooterButtonsStyle },
        react_1.default.createElement(widgets_1.ButtonWidget, { disabled: submitDisabled, onClick: hideDialog }, catalog_messages_1.Messages.Cancel()),
        react_1.default.createElement(widgets_1.ButtonWidget, { disabled: submitDisabled, showSpinner: submit.isLoading, onClick: () => submit.mutate({
                labelContent: { message: '', elevated: false, value: labelValue, active: false },
                contentItems: [contentItem],
                luid,
                delayOverride: 0,
            }), buttonType: widgets_1.ButtonType.Destructive }, catalog_messages_1.Messages.Remove())));
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(widgets_1.DialogWidget, { testId: 'delete-custom-label-test-id', isModal: true, showCloseButton: true, isGlassTransparent: false, shouldReturnFocusOnClose: true, onCloseRequest: hideDialog, handleGlassClick: hideDialog, type: widgets_1.DialogType.NormalWithTitleBar, positioningBehavior: widgets_1.DialogPositioningBehavior.Draggable, dialogWidth: 400, title: catalog_messages_1.Messages.CustomLabelDialogDeleteDialogTitle(), footerContent: footerContent },
            react_1.default.createElement("span", { style: { fontWeight: 'lighter' } }, confirmDeleteText))));
}
exports.DeleteCustomLabelDialog = DeleteCustomLabelDialog;
// Dropdown helpers
function groupLabelsByCategory(labels) {
    return ((0, LabelHelpers_1.groupBy)(labels, i => { var _a; return (_a = i.category) !== null && _a !== void 0 ? _a : ''; }));
}
function getLabelValueDescription(labelName, catName, labelDesc, catDesc) {
    return catName + ': ' + catDesc + '  \n' + labelName + ': ' + labelDesc + ' ';
}
function mapCustomLabelsAsListItems({ allLabelValues, currentLabels, categories }) {
    const labelValues = [...allLabelValues].sort((a, b) => (0, LabelValueSorting_1.sortLabelValues)(Object.assign(Object.assign({}, a), { value: a.name }), Object.assign(Object.assign({}, b), { value: b.name })));
    const descriptions = (0, labelCategories_1.reduceCategoryDescriptions)(categories.filter(labelCategories_1.isCustomLabelCategory));
    const valuesByCategory = groupLabelsByCategory(labelValues);
    // Delete built-in categories
    inBuiltCategories.forEach((value) => {
        valuesByCategory.delete(value);
    });
    const listItems = [];
    valuesByCategory.forEach((labelvalues, category) => {
        listItems.push({ value: category, group: true });
        labelvalues.forEach((value) => {
            const disabled = currentLabels.includes(value.name) ? true : false;
            listItems.push({
                value: value.name,
                tooltip: getLabelValueDescription(value.name, category, value.description, descriptions[category]),
                disabled: disabled,
            });
        });
        listItems.push({ separator: true });
    });
    // Last separator not needed
    listItems.pop();
    return listItems;
}
function useCurrentLabelsOnContent(contentItem) {
    const currentLabelsQueryFn = (0, catalog_core_1.useRestFn)(Labels_1.getLabels);
    const { data } = (0, react_query_1.useQuery)(['labels', contentItem.contentType, contentItem.contentId], () => currentLabelsQueryFn({ contentItems: [contentItem], transform: (t) => t }));
    const labels = data === null || data === void 0 ? void 0 : data.get({
        contentId: contentItem.contentId,
        contentType: contentItem.contentType.toLowerCase(),
    });
    const currentLabels = [];
    labels === null || labels === void 0 ? void 0 : labels.forEach(label => {
        if (!isInBuiltCategory(label.category))
            currentLabels.push(label.value);
    });
    return currentLabels;
}
function useCustomLabelsDropdown(contentItem) {
    var _a;
    const labelCategoriesQueryFn = (0, catalog_core_1.useRestFnNoParams)(labelCategories_1.getLabelCategories);
    const labelValuesQueryFn = (0, catalog_core_1.useRestFnNoParams)(labelValues_1.getLabelValues);
    const currentLabels = useCurrentLabelsOnContent(contentItem);
    const [labelValues, setValues] = react_1.default.useState(new Set());
    const { data: categories, isLoading: categoriesLoading } = (0, react_query_1.useQuery)(['labelCategories'], labelCategoriesQueryFn);
    const { data: allLabelValues, isLoading: labelValuesLoading } = (0, react_query_1.useQuery)(['labelValues'], labelValuesQueryFn);
    const handleChange = (value) => {
        // hack to act like single select while preserving pre-existing applied labels
        let selectedValues = new Set(labelValues);
        if (selectedValues.has(value)) {
            selectedValues.delete(value);
        }
        else {
            selectedValues = new Set();
            selectedValues.add(value);
        }
        setValues(selectedValues);
    };
    const data = mapCustomLabelsAsListItems({
        allLabelValues: allLabelValues !== null && allLabelValues !== void 0 ? allLabelValues : [],
        currentLabels,
        categories: categories !== null && categories !== void 0 ? categories : [],
    });
    const hasAnyCustomLabelValues = !!(allLabelValues === null || allLabelValues === void 0 ? void 0 : allLabelValues.find(t => !isInBuiltCategory(t.category)));
    const element = (react_1.default.createElement("div", { style: { width: '100%' } },
        react_1.default.createElement(widgets_1.MultiselectDropdownWidget, { label: '', onChange: handleChange, disabled: categoriesLoading || (!hasAnyCustomLabelValues), type: widgets_1.DropdownButtonType.Outline, value: labelValues, testId: 'custom-label-dropdown', id: 'custom-label-dropdown', style: { fontWeight: 'lighter', textOverflow: 'ellipsis', overflow: 'hidden' }, list: react_1.default.createElement(widgets_1.LocallyFilteringList, { items: categoriesLoading
                    ? [{
                            value: 'spinner',
                            content: (react_1.default.createElement(widgets_1.ActivitySpinnerWidget, { spinnerSize: widgets_1.ActivitySpinnerSize.Small, testId: 'custom-label-value' })),
                        }]
                    : data !== null && data !== void 0 ? data : [], style: { fontWeight: 'lighter', maxHeight: 300, maxWidth: '100%' } }) },
            react_1.default.createElement(CustomLabelsMultiselectDropdownChildren, { loading: labelValuesLoading, labelValues: data }))));
    const labelValue = (_a = Array.from(labelValues).pop()) !== null && _a !== void 0 ? _a : '';
    return [hasAnyCustomLabelValues, labelValue, element];
}
exports.useCustomLabelsDropdown = useCustomLabelsDropdown;
function CustomLabelsMultiselectDropdownChildren({ loading, labelValues }) {
    if (loading) {
        return react_1.default.createElement(react_1.default.Fragment, null);
    }
    return react_1.default.createElement(react_1.default.Fragment, null, labelValues.length > 0 ? catalog_messages_1.Messages.CustomLabelDropdownHint() : catalog_messages_1.Messages.CustomLabelDropdownNoValues());
}
// Main Dialog body helpers
function ContentNameAndIcon({ contentName, contentIcon, contentIconTitle, contentType }) {
    return contentName
        ? (react_1.default.createElement("div", { style: { display: 'inline', marginBottom: 16, fontSize: 16 } },
            contentType === 'column'
                ? (react_1.default.createElement("div", { style: Styles_1.ContentIconStyle, title: contentIconTitle }, contentIcon))
                : (react_1.default.createElement("div", { style: Styles_1.ContentIconStyle }, (0, LabelHelpers_1.assetTableauIconForType)(contentType, undefined, 16))),
            contentName))
        : null;
}
exports.ContentNameAndIcon = ContentNameAndIcon;
function DropDownLabelWithTooltip() {
    return (react_1.default.createElement("div", { style: { marginBottom: 5, marginTop: 16 } },
        react_1.default.createElement("span", { style: { marginRight: 5, fontWeight: 'lighter', fontSize: '14px' } }, catalog_messages_1.Messages.CustomLabelDropdownLabel()),
        react_1.default.createElement(widgets_1.Tooltip, { tooltipContent: react_1.default.createElement("span", { style: { fontWeight: 'lighter' } }, catalog_messages_1.Messages.CustomLabelDialogTooltipContent()) },
            react_1.default.createElement(NotificationInfoOutlineIcon_1.NotificationInfoOutlineIcon, { size: 14, alt: catalog_messages_1.Messages.CustomLabelDialogTooltip() }))));
}
exports.DropDownLabelWithTooltip = DropDownLabelWithTooltip;
function NoCustomLabelsInfoText() {
    return (react_1.default.createElement("div", { style: { marginTop: 16, fontSize: 12 } },
        react_1.default.createElement(NotificationInfoOutlineIcon_1.NotificationInfoOutlineIcon, { size: 14, alt: catalog_messages_1.Messages.CustomLabelDialogNoCustomLabelsInfoIcon() }),
        react_1.default.createElement("span", { style: { paddingLeft: 5 } }, catalog_messages_1.Messages.CustomLabelDialogNoCustomLabelsInfo())));
}
exports.NoCustomLabelsInfoText = NoCustomLabelsInfoText;
//# sourceMappingURL=CustomLabelDialogHelpers.js.map